import React, { useState, useEffect } from 'react';
import Cookies from 'js-cookie';
import {
    Alert,
    Avatar,
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    TextField,
} from '@mui/material';
import PersonIcon from '@mui/icons-material/Person';
import { uploadImage } from '../utils/uploadImage';

const AuthorFormDialog = ({ open, onClose, author, onSaved }) => {
    const isEdit = Boolean(author?.id);
    const [form, setForm] = useState({ name: '', bio: '', avatar_url: '' });
    const [imageFile, setImageFile] = useState(null);
    const [previewUrl, setPreviewUrl] = useState(null);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (open) {
            setForm({ name: author?.name || '', bio: author?.bio || '', avatar_url: author?.avatar_url || '' });
            setImageFile(null);
            setPreviewUrl(null);
            setError(null);
        }
    }, [open, author]);

    const handleImageChange = (e) => {
        const file = e.target.files[0];
        if (!file) return;
        if (previewUrl) URL.revokeObjectURL(previewUrl);
        setImageFile(file);
        setPreviewUrl(URL.createObjectURL(file));
    };

    const handleSubmit = async () => {
        if (!form.name.trim()) { setError('Name is required'); return; }
        setSubmitting(true);
        setError(null);
        try {
            let avatarUrl = form.avatar_url;
            if (imageFile) {
                const [, cdnUrl] = await new Promise((resolve, reject) =>
                    uploadImage(imageFile, reject, resolve)
                );
                avatarUrl = cdnUrl;
            }

            const payload = { name: form.name.trim(), bio: form.bio.trim() || null, avatar_url: avatarUrl || null };
            const url = isEdit
                ? `${process.env.REACT_APP_BACKEND_ADDRESS}/authors/${author.id}`
                : `${process.env.REACT_APP_BACKEND_ADDRESS}/authors`;
            const res = await fetch(url, {
                method: isEdit ? 'PUT' : 'POST',
                headers: {
                    'Authorization': 'Bearer ' + Cookies.get('token'),
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(payload),
            });
            if (!res.ok) throw new Error('Failed to save author');
            const saved = await res.json();
            onSaved(saved);
            onClose();
        } catch (e) {
            setError(e.message);
        } finally {
            setSubmitting(false);
        }
    };

    const avatarSrc = previewUrl || form.avatar_url || '/default-author.svg';

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle sx={{ fontWeight: 700, display: 'flex', alignItems: 'center', gap: 1 }}><PersonIcon sx={{ color: 'primary.main' }} />{isEdit ? 'Edit Author' : 'New Author'}</DialogTitle>
            <DialogContent>
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, mt: 1 }}>
                    <Avatar src={avatarSrc} sx={{ width: 96, height: 96 }} />
                    <Button component="label" variant="outlined" size="small">
                        {form.avatar_url || imageFile ? 'Change Photo' : 'Upload Photo'}
                        <input type="file" accept="image/*" hidden onChange={handleImageChange} />
                    </Button>
                </Box>
                <TextField
                    label="Name"
                    fullWidth
                    required
                    value={form.name}
                    onChange={(e) => setForm(f => ({ ...f, name: e.target.value }))}
                    sx={{ mt: 2 }}
                />
                <TextField
                    label="Biography"
                    fullWidth
                    multiline
                    rows={4}
                    value={form.bio}
                    onChange={(e) => setForm(f => ({ ...f, bio: e.target.value }))}
                    sx={{ mt: 2 }}
                    placeholder="Tell the story of this author and their recipes…"
                />
                {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
            </DialogContent>
            <DialogActions sx={{ justifyContent: 'center', pb: 2 }}>
                <Button onClick={onClose} disabled={submitting}>Cancel</Button>
                <Button variant="contained" onClick={handleSubmit} disabled={submitting}>
                    {submitting ? <CircularProgress size={20} /> : isEdit ? 'Save' : 'Create Author'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default AuthorFormDialog;
